import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { useViewTransition } from '@shopmy/ui';

import { getScriptRunConfigs } from '../../api/adminScriptRunConfigs';
import { triggerScriptRun, getScriptRunHistory, getScriptRunLogs, getScriptRunById } from '../../api/adminScriptRuns';
import { fetchUsers } from '../../api/users';
import { parseJson } from '../../helpers/helpers';
import { canCurrentUserRunScript, getScriptArgsSchema, getScriptRunnerPath } from '../../helpers/script_runner_helpers';
import ScriptRunnerConfiguration from './ScriptRunnerConfiguration';
import ScriptRunnerConfigurationForm from './ScriptRunnerConfigurationForm';
import ScriptRunnerResults from './ScriptRunnerResults';
import ScriptRunnerHistory from './ScriptRunnerHistory';
import ScriptRunnerSelection from './ScriptRunnerSelection';
import './ScriptRunner.scss';

const ScriptRunner = () => {
  const currentUser = useSelector(state => state.users.currentUser);
  const { scriptId, runId } = useParams();
  const history = useHistory();
  const [configs, setConfigs] = useState([]);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);
  const [configError, setConfigError] = useState(null);

  const [argValues, setArgValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [runError, setRunError] = useState(null);
  const [runSuccess, setRunSuccess] = useState(null);

  const [historyRuns, setHistoryRuns] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [adminUsersById, setAdminUsersById] = useState({});

  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedRunLogs, setSelectedRunLogs] = useState('');
  const [isLoadingSelectedRunLogs, setIsLoadingSelectedRunLogs] = useState(false);

  const viewTransition = useViewTransition();

  const availableConfigs = configs.filter(config => config.isEnabled !== false && canCurrentUserRunScript(config, currentUser));
  const selectedConfig = availableConfigs.find(config => String(config.id) === scriptId);
  const selectedConfigId = selectedConfig?.id;
  const selectedRunId = selectedRun?.id;
  const selectedRunUser = selectedRun?.TriggeredByUser_id ? adminUsersById[selectedRun.TriggeredByUser_id] : null;
  const canRunSelectedScript = canCurrentUserRunScript(selectedConfig, currentUser);
  const areInputsReadOnly = Boolean(selectedRun) || !canRunSelectedScript;
  const argsSchema = getScriptArgsSchema(selectedConfig);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingConfigs(true);
    setConfigError(null);

    getScriptRunConfigs()
      .then(res => {
        if (!isMounted) return;
        setConfigs(Array.isArray(res) ? res : res?.data || res?.configurations || []);
      })
      .catch(err => {
        if (!isMounted) return;
        console.error('Failed to load script configs:', err);
        setConfigError(typeof err === 'string' ? err : 'Failed to load script configurations.');
      })
      .finally(() => {
        if (isMounted) setIsLoadingConfigs(false);
      });

    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  useEffect(() => {
    if (!scriptId || isLoadingConfigs || configError || selectedConfigId) return;
    const scriptExists = configs.some(config => String(config.id) === scriptId);
    window.ALERT.error(scriptExists ? `You don't have access to script #${scriptId}.` : `Script #${scriptId} not found.`);
    history.replace(getScriptRunnerPath());
  }, [scriptId, isLoadingConfigs, configError, selectedConfigId, configs, history]);

  useEffect(() => {
    let isMounted = true;

    fetchUsers({ userType: 'admins', limit: 1000 })
      .then(res => {
        if (!isMounted) return;
        const users = res?.users || res?.data?.users || (Array.isArray(res) ? res : []);
        setAdminUsersById(
          users.reduce((usersById, user) => {
            usersById[user.id] = user;
            return usersById;
          }, {})
        );
      })
      .catch(err => {
        console.error('Failed to load admin users:', err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchHistory = useCallback(configId => {
    if (!configId) {
      setHistoryRuns([]);
      return;
    }

    setIsLoadingHistory(true);
    getScriptRunHistory(configId)
      .then(res => {
        const runs = res?.data?.runs || res?.runs || (Array.isArray(res) ? res : []);
        setHistoryRuns(runs);
        setSelectedRun(currentRun => {
          if (!currentRun?.id) return currentRun;
          return runs.find(run => String(run.id) === String(currentRun.id)) || currentRun;
        });
      })
      .catch(err => {
        console.error('Failed to fetch script run history:', err);
      })
      .finally(() => {
        setIsLoadingHistory(false);
      });
  }, []);

  useEffect(() => {
    if (selectedConfigId) {
      fetchHistory(selectedConfigId);
    } else {
      setHistoryRuns([]);
    }
    setSelectedRun(null);
    setSelectedRunLogs('');
    setArgValues({});
    setRunError(null);
    setRunSuccess(null);
  }, [selectedConfigId, fetchHistory]);

  useEffect(() => {
    if (!selectedConfigId) return undefined;

    const intervalId = window.setInterval(() => {
      fetchHistory(selectedConfigId);
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [fetchHistory, selectedConfigId]);

  const fetchSelectedRunLogs = useCallback(id => {
    setIsLoadingSelectedRunLogs(true);
    return Promise.all([getScriptRunLogs(id), getScriptRunById(id).catch(() => null)])
      .then(([logsRes, runRes]) => {
        const latestRun = runRes?.run || runRes?.data || runRes;
        if (latestRun?.id) {
          setSelectedRun(currentRun => (String(currentRun?.id) === String(latestRun.id) ? latestRun : currentRun));
        }
        const logs = typeof logsRes === 'string' ? logsRes : logsRes?.logs ?? (typeof logsRes?.data === 'string' ? logsRes.data : '');
        setSelectedRunLogs(logs || 'No logs found.');
      })
      .catch(err => {
        console.error('Failed to fetch selected run logs:', err);
        setSelectedRunLogs('Failed to load logs.');
      })
      .finally(() => {
        setIsLoadingSelectedRunLogs(false);
      });
  }, []);

  useEffect(() => {
    if (!selectedRun?.id) {
      setSelectedRunLogs('');
      setIsLoadingSelectedRunLogs(false);
      return undefined;
    }

    fetchSelectedRunLogs(selectedRun.id);
    if (selectedRun.status !== 'running') return undefined;

    const intervalId = window.setInterval(() => {
      fetchSelectedRunLogs(selectedRun.id);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [fetchSelectedRunLogs, selectedRun?.id, selectedRun?.status]);

  const applyRunSelection = useCallback(run => {
    setSelectedRun(run);
    setArgValues(parseJson(run.args) || {});
    setRunError(null);
  }, []);

  const clearRunSelection = useCallback(() => {
    setSelectedRun(null);
    setSelectedRunLogs('');
    setArgValues({});
    setRunError(null);
    setRunSuccess(null);
  }, []);

  // Deep links and back/forward navigation change the run in the URL without going through a click handler.
  useEffect(() => {
    if (!selectedConfigId) return undefined;
    if (!runId) {
      if (selectedRunId) clearRunSelection();
      return undefined;
    }
    if (String(selectedRunId) === runId) return undefined;

    let isCancelled = false;
    getScriptRunById(runId)
      .then(res => {
        if (isCancelled) return;
        const run = res?.run || res?.data || res;
        if (!run?.id) throw new Error(`Run #${runId} not found.`);
        if (String(run.AdminScriptConfiguration_id) !== String(selectedConfigId)) throw new Error(`Run #${runId} is not a run of this script.`);
        applyRunSelection(run);
      })
      .catch(err => {
        if (isCancelled) return;
        window.ALERT.error(err?.message || `Run #${runId} not found.`);
        history.replace(getScriptRunnerPath(selectedConfigId));
      });

    return () => {
      isCancelled = true;
    };
  }, [runId, selectedConfigId, selectedRunId, applyRunSelection, clearRunSelection, history]);

  const navigateTo = path => {
    if (history.location.pathname !== path) history.push(path);
  };
  const handleSelectConfig = configId => {
    viewTransition(() => navigateTo(getScriptRunnerPath(configId)));
  };
  const handleBackToSelection = () => {
    viewTransition(() => navigateTo(getScriptRunnerPath()));
  };

  // Push the URL before touching state so the URL-sync effect never sees a selected run without a run param.
  const handleSelectRun = (run, options = {}) => {
    viewTransition(() => {
      navigateTo(getScriptRunnerPath(selectedConfigId, run.id));
      applyRunSelection(run);
      if (!options.preserveSuccess) {
        setRunSuccess(null);
      }
    });
  };

  const handleClearSelectedRun = e => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    navigateTo(getScriptRunnerPath(selectedConfigId));
    clearRunSelection();
  };
  const handleCreateRunWithInputs = () => {
    const copiedArgs = parseJson(selectedRun.args) || {};
    navigateTo(getScriptRunnerPath(selectedConfigId));
    clearRunSelection();
    setArgValues(copiedArgs);
  };

  const handleArgChange = (flag, value) => {
    setArgValues(prev => ({
      ...prev,
      [flag]: value
    }));
  };

  const handleSubmit = e => {
    e?.preventDefault?.();
    if (!selectedConfig) return;

    setRunError(null);
    setRunSuccess(null);

    if (!canRunSelectedScript) {
      setRunError('You do not have permission to run this script.');
      return;
    }

    const missingRequired = argsSchema.filter(arg => {
      const value = argValues[arg.flag];
      return arg.required && (value === undefined || value === '' || (Array.isArray(value) && value.length === 0));
    });

    if (missingRequired.length > 0) {
      setRunError(`Required fields missing: ${missingRequired.map(arg => arg.name).join(', ')}`);
      return;
    }

    const formattedArgs = {};
    argsSchema.forEach(arg => {
      const val = argValues[arg.flag];
      if (val !== undefined && val !== '') {
        if (arg.type === 'number') {
          formattedArgs[arg.flag] = Number(val);
        } else if (arg.type === 'boolean' || arg.takesValue === false) {
          formattedArgs[arg.flag] = Boolean(val);
        } else {
          formattedArgs[arg.flag] = val;
        }
      }
    });

    setIsSubmitting(true);
    const linkedDryRunId = selectedRun?.isDryRun ? Number(selectedRun.id) : null;

    triggerScriptRun({
      AdminScriptConfiguration_id: selectedConfig.id,
      args: formattedArgs,
      isDryRun: Boolean(selectedConfig.mustDryRun && !linkedDryRunId),
      DryRunAdminScriptRun_id: linkedDryRunId
    })
      .then(res => {
        const run = res?.data?.run || res?.run;
        setRunSuccess(`Script run triggered successfully${run?.id ? ` (#${run.id})` : ''}`);
        fetchHistory(selectedConfig.id);
        if (run) {
          handleSelectRun(run, { preserveSuccess: true });
        }
      })
      .catch(err => {
        console.error('Failed to trigger script run:', err);
        setRunError(typeof err === 'string' ? err : 'Failed to trigger script run.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (!selectedConfig) {
    return (
      <div className='script-runner-outer script-runner-selection-outer'>
        <ScriptRunnerSelection
          configs={availableConfigs}
          isLoadingConfigs={isLoadingConfigs}
          configError={configError}
          onSelectConfig={handleSelectConfig}
        />
      </div>
    );
  }

  return (
    <div className='script-runner-outer script-runner-config-outer'>
      <main className='script-runner-main'>
        <button type='button' className='script-runner-breadcrumb' onClick={handleBackToSelection}>
          <FontAwesomeIcon icon={faArrowLeft} />
          All Scripts
        </button>

        <ScriptRunnerConfiguration selectedConfig={selectedConfig} canRunSelectedScript={canRunSelectedScript} />

        {configError && <div className='script-runner-alert script-runner-alert-error'>{configError}</div>}

        <ScriptRunnerResults
          selectedRun={selectedRun}
          selectedRunUser={selectedRunUser}
          selectedRunLogs={selectedRunLogs}
          isLoadingSelectedRunLogs={isLoadingSelectedRunLogs}
          onRefreshLogs={fetchSelectedRunLogs}
          onPromoteDryRun={handleSubmit}
          isSubmitting={isSubmitting}
          canRunSelectedScript={canRunSelectedScript}
        />

        <ScriptRunnerConfigurationForm
          selectedConfig={selectedConfig}
          selectedRun={selectedRun}
          argsSchema={argsSchema}
          argValues={argValues}
          onArgChange={handleArgChange}
          areInputsReadOnly={areInputsReadOnly}
          runSuccess={runSuccess}
          runError={runError}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onClearSelectedRun={handleClearSelectedRun}
          onCreateRunWithInputs={handleCreateRunWithInputs}
          onCancel={handleBackToSelection}
          canRunSelectedScript={canRunSelectedScript}
        />
      </main>

      <ScriptRunnerHistory
        selectedConfig={selectedConfig}
        historyRuns={historyRuns}
        selectedRun={selectedRun}
        currentUserId={currentUser?.id}
        adminUsersById={adminUsersById}
        isLoadingHistory={isLoadingHistory}
        onRefresh={fetchHistory}
        onSelectRun={handleSelectRun}
        onClearSelectedRun={handleClearSelectedRun}
      />
    </div>
  );
};

export default ScriptRunner;
